import { TFunction } from "i18next"
import { TierIdEnum } from "@/entities/subscription/subscription.types"
import {
  CurrencyEnum,
  CurrencyUnitEnum,
  Invoice,
  InvoiceStatusEnum,
  InvoiceTypeEnum,
} from "./payment.types"

export enum PaymentQueryKeysEnum {
  FetchInvoiceInfo = "fetchInvoiceInfo",
  FetchInvoiceDetailInfo = "fetchInvoiceDetailInfo",
  FetchStripeCheckoutInfo = "fetchStripeCheckoutInfo",
  RequestTossCustomerKey = "requestTossCustomerKey",
  RequestTossBillingKey = "requestTossBillingKey",
  RequestStripeKey = "requestStripeKey",
}

export const ISSUER_CODE_LIST: Record<string, string> = {
  "3K": "기업비씨",
  "46": "광주",
  "71": "롯데",
  "30": "산업",
  "31": "BC",
  "51": "삼성",
  "38": "새마을",
  "41": "신한",
  "62": "신협",
  "36": "씨티",
  "33": "우리",
  W1: "우리",
  "37": "우체국",
  "39": "저축",
  "35": "전북",
  "42": "제주",
  "15": "카카오뱅크",
  "3A": "케이뱅크",
  "24": "토스뱅크",
  "21": "하나",
  "61": "현대",
  "11": "국민",
  "91": "농협",
  "34": "수협",
  "6D": "다이너스",
  "4M": "마스터",
  "3C": "유니온페이",
  "7A": "AMEX",
  "4J": "JCB",
  "4V": "VISA",
}

export const renderInvoiceSummary = (
  invoiceType: InvoiceTypeEnum,
  tierId: TierIdEnum,
  t: TFunction
) => {
  switch (invoiceType) {
    case InvoiceTypeEnum.MtMonthlyPayment:
      return (
        <>
          <span className="font-semibold">
            {t("settings.invoicePlanSubscription", {
              plan: tierId,
            })}
          </span>
          <span>({t("settings.invoicePlanSubscription2")})</span>
        </>
      )
    case InvoiceTypeEnum.MtAdditionalCharge:
      return t("settings.invoiceAdditionalUsage")
    case InvoiceTypeEnum.MtSubscriptionUpgrade:
      return (
        <p className="font-semibold">
          {t("settings.invoicePlanUpgrade", { plan: tierId })}
        </p>
      )
  }
}

export const getInvoiceStatusI18nKey = (status: InvoiceStatusEnum) => {
  switch (status) {
    case InvoiceStatusEnum.Completed:
      return "plan.paymentSuccessHeader"
    case InvoiceStatusEnum.Canceled:
      return "plan.paymentCanceledHeader"
    case InvoiceStatusEnum.PartiallyRefunded:
      return "plan.paymentPartiallyRefundedHeader"
  }
}

export const getCurrencyUnit = (currency: CurrencyEnum) => {
  return currency === CurrencyEnum.Krw
    ? CurrencyUnitEnum.Krw
    : CurrencyUnitEnum.Usd
}

export const isGlobalPaidUser = (currency: CurrencyEnum) => {
  return currency === CurrencyEnum.Usd
}

export const monthlyPaymentBeforeTax = (payment: Invoice): number => {
  return isGlobalPaidUser(payment.currency)
    ? Number(payment.originalPayment) / 100
    : (Number(payment.originalPayment) * 10) / 11
}

export const additionalChargeBeforeTax = (
  payment: Invoice,
  unitPrice: number
): number => {
  return Number(payment.usage) * unitPrice
}

export const isMonthlyPaymentMode = (payment: Invoice): boolean =>
  [
    InvoiceTypeEnum.MtMonthlyPayment,
    InvoiceTypeEnum.MtSubscriptionUpgrade,
  ].includes(payment?.invoiceType as InvoiceTypeEnum)

export const totalPrice = (payment: Invoice, unitPrice: number): number =>
  isMonthlyPaymentMode(payment)
    ? monthlyPaymentBeforeTax(payment)
    : additionalChargeBeforeTax(payment, unitPrice)

export const finalPaymentPrice = (payment: Invoice): number =>
  isGlobalPaidUser(payment.currency)
    ? Number(payment.finalPayment) / 100
    : Number(payment.finalPayment)
